import { DateTime } from "luxon";

import { pendingAppointmentStatuses } from "@/packages/shared/src/models/appointment";
import { resolveEhrAppointmentIdNamespace } from "@/src/lib/ehr/scheduling/appointment-identity";
import { commsLogger } from "@/src/lib/logging/comms-logger";
import { Appointment, AppointmentCreatedBy } from "@/src/models/appointment";
import type { AppointmentSlot } from "@/src/models/comms/scheduling-schemas";
import { Group } from "@/src/models/group";
import {
  createAppointment,
  getAppointment,
  getAppointmentByEhrId,
  getAllAppointmentsByPatientIds,
  updateAppointment,
} from "@/src/repo/appointment";
import { getPractitionerByEhrId } from "@/src/repo/practitioner";
import { getAppointmentBookedClosuresByPatientId } from "@/src/repo/sms-conversation-event";

const logger = commsLogger("appointment");

/**
 * True when the appointment is in a pending/scheduled status and dated in the future — i.e. one the
 * patient still has on the books. Pure: pass appointments you've already fetched.
 */
export function isUpcomingAppointment(appointment: Appointment): boolean {
  return isUpcomingAppointmentAt(appointment, DateTime.now());
}

export function isUpcomingAppointmentAt(appointment: Appointment, now: DateTime): boolean {
  if (!appointment.status || !pendingAppointmentStatuses.includes(appointment.status)) return false;
  if (!appointment.appointmentDatetime) return false;

  const appointmentDateTime = DateTime.fromISO(appointment.appointmentDatetime, { setZone: true });

  return appointmentDateTime.isValid && appointmentDateTime > now;
}

/** Athena can return multiple open-slot records for the same wall-clock time; keep one per provider/start. */
export function dedupeAppointmentSlots(slots: AppointmentSlot[]): AppointmentSlot[] {
  const seen = new Set<string>();
  const deduped: AppointmentSlot[] = [];

  for (const slot of slots) {
    const key = `${slot.practitioner.id ?? slot.practitioner.ehrId}:${slot.start}:${slot.location?.id ?? ""}`;

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    deduped.push(slot);
  }

  return deduped;
}

const EHR_UPDATE_FIELDS: (keyof Appointment)[] = [
  "appointmentDatetime",
  "ehrId",
  "ehrReason",
  "ehrStatus",
  "ehrType",
  "patientId",
  "practitionerId",
  "providerEhrId",
  "status",
  "type",
];

function pickEhrUpdatePayload(appointment: Appointment, existing: Appointment): Partial<Appointment> {
  const payload: Partial<Appointment> = {};

  for (const key of EHR_UPDATE_FIELDS) {
    (payload as Record<string, unknown>)[key] = appointment[key];
  }

  const ehrIdNamespace = resolveEhrAppointmentIdNamespace(existing.ehrIdNamespace, appointment.ehrIdNamespace);

  if (ehrIdNamespace !== null) {
    payload.ehrIdNamespace = ehrIdNamespace;
  }

  return payload;
}

const APPOINTMENT_AFTER_CLOSE_WINDOW_MS = 180 * 24 * 60 * 60 * 1000;

const VALERIE_ORIGINATED: AppointmentCreatedBy[] = [
  AppointmentCreatedBy.VALERIE_SMS,
  AppointmentCreatedBy.VALERIE_VOICE,
];

/**
 * Attribution is conversation-first: find SMS conversations we closed as "appointment booked", then
 * see if this appointment exists after one of them (within 180 days) and is the only future one we
 * attribute to SMS. Appointments we already attributed to Valerie (SMS or voice) are always
 * preserved when re-processing from EHR (e.g. cron pull), so our product-created appointments stay
 * attributed.
 */
export async function getAttribution(appointment: Appointment, group: Group): Promise<AppointmentCreatedBy> {
  const appointmentId = appointment.id;

  if (appointment.createdBy != null && VALERIE_ORIGINATED.includes(appointment.createdBy)) {
    return appointment.createdBy;
  }

  const appointmentDatetime = appointment.appointmentDatetime;

  if (!appointmentDatetime || new Date(appointmentDatetime).getTime() <= Date.now()) {
    return AppointmentCreatedBy.EXTERNAL;
  }

  if (!appointment.patientId) {
    return AppointmentCreatedBy.EXTERNAL;
  }

  const bookedClosures = await getAppointmentBookedClosuresByPatientId(appointment.patientId, group);

  const appointmentTime = new Date(appointmentDatetime).getTime();
  const appointmentExistsAfterClosure = bookedClosures.some((event) => {
    const closedAt = new Date(event.createdAt).getTime();

    return appointmentTime >= closedAt && appointmentTime <= closedAt + APPOINTMENT_AFTER_CLOSE_WINDOW_MS;
  });

  if (!appointmentExistsAfterClosure) {
    return AppointmentCreatedBy.EXTERNAL;
  }

  const patientAppointments = await getAllAppointmentsByPatientIds([appointment.patientId], group);
  const now = Date.now();
  const patientAlreadyHasFutureValerieSmsAppointment = patientAppointments.some(
    (a) =>
      a.id !== appointment.id &&
      a.createdBy === AppointmentCreatedBy.VALERIE_SMS &&
      a.appointmentDatetime != null &&
      new Date(a.appointmentDatetime).getTime() > now
  );

  if (patientAlreadyHasFutureValerieSmsAppointment) {
    return AppointmentCreatedBy.EXTERNAL;
  }

  logger.info("EMR appointment attributed to Valerie SMS", { appointmentId });

  return AppointmentCreatedBy.VALERIE_SMS;
}

export async function createOrUpdateAppointmentFromEhr(appointment: Appointment, group: Group): Promise<Appointment> {
  if (!appointment.practitionerId && appointment.providerEhrId) {
    const practitioner = await getPractitionerByEhrId(appointment.providerEhrId, group);

    if (practitioner) {
      appointment = { ...appointment, practitionerId: practitioner.id };
    }
  }

  let existing: Appointment | null = null;

  if (appointment.ehrId) {
    existing = await getAppointmentByEhrId(appointment.ehrId, group);
  }

  if (!existing && appointment.id) {
    existing = await getAppointment(appointment.id, group);
  }

  if (existing) {
    const ehrPayload = pickEhrUpdatePayload(appointment, existing);

    return updateAppointment(existing.id, ehrPayload, group);
  }

  const createdBy = await getAttribution(appointment, group);

  return createAppointment({ ...appointment, createdBy }, group);
}
